import { useMemo, useState } from "react";

import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { alpha } from "@mui/material/styles";
import { red } from "@mui/material/colors";
import { isEmpty } from "lodash";

import ColorUtil from "@plannel/pages/utils/color-util";
import StatPill from "@plannel/components/ui/StatPill";
import { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { FS } from "@plannel/utils/font-util";

/*
  직책 계층 보드 — "레벨은 아래로, 계보는 옆으로".

  기존 ReactFlow 오거나이제이션 차트는 노드를 절대좌표로 흩뿌려서, 화면 폭이 좁아지면
  줌아웃 말고는 읽을 방법이 없었다(그래서 전체화면 버튼이 필요했다). 여기서는 트리를 두 축으로 나눈다.
    · 세로(트렁크)  — 갈래가 하나뿐인 상위 구간. 왼쪽 거터의 L0/L1… 이 곧 깊이다.
    · 가로(브랜치)  — 처음으로 갈래가 갈리는 지점의 자식들. 한 갈래 = 한 컬럼, 색 띠 하나.
  덕분에 컬럼은 그냥 세로 리스트라 폭이 줄면 스크롤될 뿐 구조가 무너지지 않는다.

  거터 라벨은 트렁크 행에만 붙인다. 브랜치 안쪽은 갈래마다 깊이가 제각각이라
  거터가 한 줄로 "이 행은 L3" 이라고 말하면 거짓말이 되기 때문 — 대신 카드마다 Ln 칩을 단다.
*/
const GUTTER_WIDTH = 46;

const buildBoard = (postns) => {
  if (isEmpty(postns)) {
    return { trunk: [], branches: [] };
  }

  const nodes = new Map();
  for (const postn of postns) {
    nodes.set(postn.postnCd, {
      cd: postn.postnCd,
      name: postn.name,
      finalApvlFlg: postn.finalApvlFlg,
      postn,
      children: [],
    });
  }

  const roots = [];
  for (const postn of postns) {
    const parentCd = postn?.parentPostn?.postnCd;
    const node = nodes.get(postn.postnCd);
    if (parentCd && nodes.has(parentCd)) {
      nodes.get(parentCd).children.push(node);
    } else {
      roots.push(node);
    }
  }

  const setDepth = (node, depth) => {
    node.depth = depth;
    for (const child of node.children) {
      setDepth(child, depth + 1);
    }
  };
  roots.forEach(root => setDepth(root, 0));

  // 갈래가 갈릴 때까지 내려가며 트렁크를 모은다. 루트가 여럿이면 트렁크 없이 바로 브랜치다.
  const trunk = [];
  let frontier = roots;
  while (frontier.length === 1 && frontier[0].children.length === 1) {
    trunk.push(frontier[0]);
    frontier = frontier[0].children;
  }
  if (frontier.length === 1) {
    trunk.push(frontier[0]);
    frontier = frontier[0].children;
  }

  const flatten = (node, rel, out) => {
    for (const child of node.children) {
      out.push({ node: child, rel });
      flatten(child, rel + 1, out);
    }
    return out;
  };

  const branches = frontier.map(node => ({
    node,
    reps: flatten(node, 0, []),
  }));

  return { trunk, branches };
};

const PositionHierarchyBoard = ({
  t, postns, ruleCountByPostn, selectedPostnCd, onSelect,
  apvlActions, onRequestApvlMenu, fullScreen, setFullScreen,
}) => {
  const [menu, setMenu] = useState(null);
  const { trunk, branches } = useMemo(() => buildBoard(postns), [postns]);

  const handleContextMenu = (e, node) => {
    e.preventDefault();
    onSelect(node.cd);
    onRequestApvlMenu(node.cd);
    setMenu({ top: e.clientY, left: e.clientX });
  };

  const closeMenu = () => setMenu(null);

  const cardSx = (selected) => ({
    position: "relative",
    boxSizing: "border-box",
    border: 1,
    borderColor: selected ? "primary.main" : "divider",
    borderRadius: 1.5,
    bgcolor: selected ? theme => alpha(theme.palette.primary.main, 0.06) : "background.paper",
    boxShadow: selected ? theme => `inset 0 0 0 1px ${theme.palette.primary.main}` : "0 1px 2px rgba(0,0,0,0.03)",
    px: 1.25,
    py: 1,
    cursor: "pointer",
    "&:hover": { borderColor: selected ? "primary.main" : "grey.400" },
  });

  const levelChipSx = {
    flexShrink: 0,
    fontSize: FS.CAPTION,
    fontWeight: 600,
    lineHeight: 1,
    px: 0.625,
    py: "3px",
    borderRadius: 0.75,
    bgcolor: "grey.100",
    color: "text.secondary",
  };

  // 카드 하나 — 이름 + Ln 칩 / 최종승인 칩 + 규칙·하위 개수. 트렁크와 브랜치가 같은 카드를 쓴다.
  const renderCard = (node, { accent } = {}) => {
    const selected = node.cd === selectedPostnCd;
    const ruleCount = ruleCountByPostn?.[node.cd] ?? 0;
    return (
      <Box
        onClick={() => onSelect(node.cd)}
        onContextMenu={(e) => handleContextMenu(e, node)}
        sx={cardSx(selected)}
      >
        {accent && (
          <Box sx={{
            position: "absolute", left: 0, top: 6, bottom: 6, width: 3,
            borderRadius: "0 3px 3px 0", bgcolor: accent,
          }} />
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: accent ? 0.75 : 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {node.name}
          </Box>
          <Box sx={levelChipSx}>{`L${node.depth}`}</Box>
        </Box>
        {/*
          카드가 말해야 할 건 "규칙 몇 개"뿐이다. 하위 직책 수는 바로 아래 컬럼·들여쓰기로 이미 보이므로
          배지로 또 세어주지 않는다. 배지는 nowrap 이라, 줄바꿈을 허용해 언어별 라벨 길이 차이도 흡수한다.
        */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.75, pl: accent ? 0.75 : 0 }}>
          {node.finalApvlFlg && (
            <Box sx={{
              ...ColorUtil.tintChipSx(red[700]),
              fontSize: FS.CAPTION, lineHeight: 1, px: 0.75, py: "3px", borderRadius: 0.75, whiteSpace: "nowrap",
            }}>
              {t("finalApvl")}
            </Box>
          )}
          <Box sx={{ flexGrow: 1, flexBasis: 0 }} />
          <StatPill label={t("rule")} value={ruleCount} accent={ruleCount > 0} sx={{ height: 19, px: 0.75, fontSize: FS.CAPTION }} />
        </Box>
      </Box>
    );
  };

  const connector = (
    <Box sx={{ width: "2px", height: 18, bgcolor: "divider", ml: "22px", flexShrink: 0 }} />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }} onClick={closeMenu}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, pb: 1, flexShrink: 0 }}>
        <Box sx={CARD_HEADER_SX}>{t("postnHrchy")}</Box>
        <StatPill label={t("postn")} value={postns?.length ?? 0} />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setFullScreen(!fullScreen)}>
          <Tooltip title={fullScreen ? t("fullScreenOff") : t("fullScreen")}>
            {fullScreen
              ? <CloseFullscreenIcon sx={{ width: 16, height: 16 }} />
              : <OpenInFullIcon sx={{ width: 16, height: 16 }} />}
          </Tooltip>
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 0.5, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 520, minHeight: "100%" }}>
          {trunk.map((node, idx) => (
            <Box key={node.cd} sx={{ display: "flex", alignItems: "stretch" }}>
              <Box sx={{ width: GUTTER_WIDTH, flexShrink: 0, pt: "2px", fontSize: FS.BODY, fontWeight: 600, color: "text.secondary" }}>
                {`L${node.depth}`}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Box sx={{ width: 320, maxWidth: "100%" }}>{renderCard(node)}</Box>
                {(idx < trunk.length - 1 || branches.length > 0) && connector}
              </Box>
            </Box>
          ))}

          {branches.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
              <Box sx={{ width: GUTTER_WIDTH, flexShrink: 0, pt: "2px", fontSize: FS.BODY, fontWeight: 600, color: "text.secondary" }}>
                {`L${branches[0].node.depth}+`}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: 1.5, alignItems: "stretch" }}>
                {branches.map((branch, idx) => {
                  const color = ColorUtil.colorSet(idx, 500);
                  return (
                    <Box key={branch.node.cd} sx={{
                      flex: 1, minWidth: 190, display: "flex", flexDirection: "column",
                      boxSizing: "border-box",
                      border: `1px solid ${alpha(color, 0.28)}`,
                      borderTop: `2px solid ${color}`,
                      borderRadius: "3px 3px 12px 12px",
                      bgcolor: alpha(color, 0.045),
                      p: 1,
                      gap: 1,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.25 }}>
                        <Box sx={{
                          flex: 1, minWidth: 0, fontSize: FS.BODY, fontWeight: 700, color,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {branch.node.name}
                        </Box>
                        <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", flexShrink: 0 }}>
                          {branch.reps.length + 1}
                        </Box>
                      </Box>

                      {renderCard(branch.node, { accent: color })}

                      {branch.reps.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                          <Box sx={{ height: "1px", bgcolor: alpha(color, 0.28), mx: 0.25 }} />
                          {branch.reps.map(({ node, rel }) => (
                            <Box key={node.cd} sx={{ position: "relative", pl: `${rel * 12 + 12}px` }}>
                              {/* 들여쓰기만으로는 어느 카드에 딸린 줄인지 안 보인다 — 레일 + 짧은 가지로 잇는다. */}
                              <Box sx={{
                                position: "absolute", left: `${rel * 12 + 3}px`, top: -6, bottom: "50%",
                                width: "1px", bgcolor: alpha(color, 0.35),
                              }} />
                              <Box sx={{
                                position: "absolute", left: `${rel * 12 + 3}px`, top: "50%",
                                width: 9, height: "1px", bgcolor: alpha(color, 0.35),
                              }} />
                              {renderCard(node)}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* 최종승인 지정/해제는 카드 우클릭으로만 닿는다 — 상시 노출하면 카드가 버튼밭이 된다. */}
      <Menu
        open={menu !== null}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menu ?? undefined}
      >
        {(apvlActions ?? []).map(action => (
          <MenuItem
            key={action.label}
            dense
            disabled={action.apvlFlg}
            onClick={() => { closeMenu(); action.effect(); }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default withTranslation()(PositionHierarchyBoard);
